// ===========================================
// hooks/usePaginatedBooks.js
// ===========================================
import { useState, useCallback, useMemo, useEffect } from 'react';

export const usePaginatedBooks = (allBooks, booksPerPage) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [lastBooksHash, setLastBooksHash] = useState('');

  // Create a hash of the current books to detect changes
  const booksHash = useMemo(() => {
    return JSON.stringify(allBooks.map(book => book.id).sort());
  }, [allBooks]);

  // Reset pagination when books change (different search/filter results)
  useEffect(() => {
    if (booksHash !== lastBooksHash) {
      setCurrentPage(1);
      setLastBooksHash(booksHash);
    }
  }, [booksHash, lastBooksHash]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(allBooks.length / booksPerPage);
  }, [allBooks.length, booksPerPage]);

  // Get books for current page range (cumulative for infinite scroll)
  const paginatedBooks = useMemo(() => {
    const startIndex = 0;
    const endIndex = currentPage * booksPerPage;
    const result = allBooks.slice(startIndex, endIndex);
    return result;
  }, [allBooks, currentPage, booksPerPage]);

  // Check if there are more books to load
  const hasMore = useMemo(() => {
    // If no books at all, no more to load
    if (allBooks.length === 0) return false;
    
    const totalShowing = currentPage * booksPerPage;
    return totalShowing < allBooks.length;
  }, [currentPage, booksPerPage, allBooks.length]);

  // Load more books
  const loadMore = useCallback(async () => {
    // Early exit conditions
    if (isLoading || allBooks.length === 0) {
      return;
    }
    
    const totalShowing = currentPage * booksPerPage;
    const hasMoreBooks = totalShowing < allBooks.length;
    
    if (!hasMoreBooks) {
      return;
    }

    setIsLoading(true);
    
    // Simulate network delay for smooth UX
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setCurrentPage(prev => prev + 1);
    setIsLoading(false);
  }, [isLoading, currentPage, booksPerPage, allBooks.length]);

  // Reset pagination manually (for search changes)
  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setLastBooksHash(''); // Force reset on next books change
  }, []);

  return {
    paginatedBooks,
    isLoading,
    hasMore,
    currentPage,
    totalPages,
    loadMore,
    resetPagination,
    // Debug info
    debugInfo: {
      totalBooks: allBooks.length,
      showingBooks: paginatedBooks.length,
      currentPage,
      totalPages,
      hasMore,
      isLoading,
      booksPerPage,
      nextPageWouldShow: (currentPage + 1) * booksPerPage
    }
  };
};